import type {
  InventoryLead,
  InventoryLeadAttachment,
  InventoryLeadNote,
  InventoryLeadStatus,
  InventoryLeadStatusHistoryEntry,
  InventoryLeadTruck,
} from '@otw/models/leads/inventoryLead'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { addDoc, deleteDoc, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore'
import { v4 as uuidv4 } from 'uuid'
import { db } from '@otw/models/db'
import { allocateInventoryLeadNumber } from 'core/leads/allocateInventoryLeadNumber'
import {
  syncContactInventoryStageAfterLeadDeleted,
  syncContactInventoryStageAfterLeadSaved,
} from 'views/SuperAdmin/Management/LeadManagement/util/contactLeadSync'

export const inventoryLeadsQueryKey = ['inventory_leads'] as const

export type CreateInventoryLeadInput = {
  contactId: string
  companyId: string
  trucks: InventoryLeadTruck[]
  status?: InventoryLeadStatus | string | null
  assignedRepId?: string | null
  note?: InventoryLeadNote | null
  createdBy?: string
}

const cleanUndefinedValues = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) cleaned[key] = value
  }
  return cleaned as Partial<T>
}

interface UseInventoryLeadsReturn {
  inventoryLeads: InventoryLead[] | undefined
  isLoading: boolean
  error: Error | null
  createInventoryLead: (input: CreateInventoryLeadInput) => Promise<InventoryLead>
  updateInventoryLead: (args: {
    id: string
    updates: Partial<
      Pick<
        InventoryLead,
        | 'contactId'
        | 'companyId'
        | 'trucks'
        | 'status'
        | 'assignedRepId'
        | 'note'
        | 'attachments'
        | 'shippingPricePerMile'
        | 'negotiationHistory'
        | 'lostReasonId'
        | 'lostReasonLabel'
        | 'lostReasonNotes'
      >
    >
  }) => Promise<InventoryLead>
  deleteInventoryLead: (id: string) => Promise<string>
  getInventoryLeadsByContactId: (contactId: string) => InventoryLead[]
  getInventoryLeadsByCompanyId: (companyId: string) => InventoryLead[]
}

type UseInventoryLeadsOptions = {
  /** When false, skip the full inventory leads list query (mutations only). Default true. */
  fetchList?: boolean
}

export const useInventoryLeads = (
  options?: UseInventoryLeadsOptions,
): UseInventoryLeadsReturn => {
  const fetchList = options?.fetchList !== false
  const { showSuccess, showError, showWarning } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()
  const queryKey = inventoryLeadsQueryKey

  const {
    data: inventoryLeads,
    isLoading,
    error,
  } = useQuery<InventoryLead[]>({
    queryKey,
    queryFn: async () => {
      const snapshot = await getDocs(db.inventory_leads)
      return snapshot.docs.map((d) => d.data())
    },
    enabled: fetchList,
    staleTime: 1000 * 60 * 5,
  })

  const createMutation = useMutation({
    mutationFn: async (input: CreateInventoryLeadInput) => {
      const now = new Date()
      const status = input.status ?? 'pendingList'
      const leadNumber = await allocateInventoryLeadNumber(now)
      const initialHistory: InventoryLeadStatusHistoryEntry = {
        id: uuidv4(),
        status,
        changedBy: userInfo?.id || input.createdBy || 'unknown',
        changedAt: now,
      }
      const payload: Omit<InventoryLead, 'id'> = {
        leadNumber,
        contactId: input.contactId,
        companyId: input.companyId,
        trucks: input.trucks,
        status,
        statusHistory: [initialHistory],
        assignedRepId: input.assignedRepId?.trim() ? input.assignedRepId.trim() : null,
        note: input.note ?? null,
        createdBy: input.createdBy || userInfo?.id || '',
        createdAt: now,
        updatedAt: now,
      }
      const ref = await addDoc(db.inventory_leads, payload as InventoryLead)
      return { ...payload, id: ref.id }
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey })
      showSuccess('Inventory lead created')
      try {
        await syncContactInventoryStageAfterLeadSaved(result, queryClient)
      } catch {
        showWarning(
          'Inventory lead created, but updating the contact inventory stage failed. Try refreshing contacts.',
        )
      }
    },
    onError: () => showError('Could not create inventory lead'),
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string
      updates: Partial<
        Pick<
          InventoryLead,
          | 'contactId'
          | 'companyId'
          | 'trucks'
          | 'status'
          | 'assignedRepId'
          | 'note'
          | 'attachments'
          | 'shippingPricePerMile'
          | 'negotiationHistory'
          | 'lostReasonId'
          | 'lostReasonLabel'
          | 'lostReasonNotes'
        >
      >
    }) => {
      const cached = inventoryLeads?.find((l) => l.id === id)
      const current = cached ?? (await getDoc(doc(db.inventory_leads, id))).data()
      if (!current) throw new Error('Inventory lead not found')
      const now = new Date()
      const updateData: Record<string, unknown> = {
        ...updates,
        updatedAt: now.toISOString(),
      }

      let statusHistory = current.statusHistory
      if (updates.status && updates.status !== current.status) {
        const historyEntry = {
          id: uuidv4(),
          status: updates.status,
          changedBy: userInfo?.id || 'unknown',
          changedAt: now,
        }
        statusHistory = [...(current.statusHistory ?? []), historyEntry]
        updateData.statusHistory = statusHistory.map((entry) => ({
          id: entry.id,
          status: entry.status,
          changedBy: entry.changedBy,
          changedAt:
            entry.changedAt instanceof Date
              ? entry.changedAt.toISOString()
              : entry.changedAt,
        }))

        // Leaving lost clears reason fields unless the caller is setting them.
        if (updates.status !== 'lost') {
          if (updates.lostReasonId === undefined) updateData.lostReasonId = null
          if (updates.lostReasonLabel === undefined) updateData.lostReasonLabel = null
          if (updates.lostReasonNotes === undefined) updateData.lostReasonNotes = null
        }
      }

      const cleaned = cleanUndefinedValues(updateData)
      await updateDoc(doc(db.inventory_leads, id), cleaned as any)

      const merged: InventoryLead = {
        ...current,
        ...updates,
        statusHistory,
        updatedAt: now,
      }
      return { merged, previousContactId: current.contactId }
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey })
      showSuccess('Inventory lead updated')
      try {
        await syncContactInventoryStageAfterLeadSaved(
          result.merged,
          queryClient,
          result.previousContactId,
        )
      } catch {
        showWarning(
          'Inventory lead updated, but updating the contact inventory stage failed. Try refreshing contacts.',
        )
      }
    },
    onError: () => showError('Could not update inventory lead'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const cached = inventoryLeads?.find((l) => l.id === id)
      const current = cached ?? (await getDoc(doc(db.inventory_leads, id))).data()
      await deleteDoc(doc(db.inventory_leads, id))
      return {
        id,
        contactId: current?.contactId ?? '',
      }
    },
    onSuccess: async (deleted) => {
      await queryClient.invalidateQueries({ queryKey })
      showSuccess('Inventory lead deleted')
      try {
        await syncContactInventoryStageAfterLeadDeleted(deleted, queryClient)
      } catch {
        showWarning(
          'Inventory lead deleted, but updating the contact inventory stage failed. Try refreshing contacts.',
        )
      }
    },
    onError: () => showError('Could not delete inventory lead'),
  })

  return {
    inventoryLeads,
    isLoading,
    error: error as Error | null,
    createInventoryLead: (input) => createMutation.mutateAsync(input),
    updateInventoryLead: async (args) => {
      const result = await updateMutation.mutateAsync(args)
      return result.merged
    },
    deleteInventoryLead: async (id) => {
      const result = await deleteMutation.mutateAsync(id)
      return result.id
    },
    getInventoryLeadsByContactId: (contactId: string) =>
      (inventoryLeads ?? []).filter((l) => l.contactId === contactId),
    getInventoryLeadsByCompanyId: (companyId: string) =>
      (inventoryLeads ?? []).filter((l) => l.companyId === companyId),
  }
}
